import {createSelector} from '@reduxjs/toolkit';
import {RootState} from '../store';
import {decodeCensorMeClaims} from '../../lib/jwtClaims';
import {requiresStrictMediaReference} from '../../backend-types/user';

export const authStateSelector = (state: RootState) => state.auth;

export const userSelector = (state: RootState) => authStateSelector(state).user;
export const appUserSelector = (state: RootState) => authStateSelector(state).appUser;
export const authLoadingSelector = (state: RootState) => authStateSelector(state).loading;
export const authInitializedSelector = (state: RootState) => authStateSelector(state).initialized;
export const authErrorSelector = (state: RootState) => authStateSelector(state).error;

export const sessionSelector = (state: RootState) => authStateSelector(state).session;
export const passwordSetConfirmedSelector = (state: RootState) => authStateSelector(state).passwordSetConfirmed;

export const setAccountPasswordApiSelector = (state: RootState) =>
  authStateSelector(state).api.setAccountPassword;
export const confirmEmailLinkApiSelector = (state: RootState) =>
  authStateSelector(state).api.confirmEmailLink;
export const updateUserProfileApiSelector = (state: RootState) => authStateSelector(state).api.updateUserProfile;
export const updateVideoEditorPreferenceApiSelector = (state: RootState) =>
  authStateSelector(state).api.updateVideoEditorPreference;
export const requestPasswordResetApiSelector = (state: RootState) =>
  authStateSelector(state).api.requestPasswordReset;
export const updatePasswordApiSelector = (state: RootState) => authStateSelector(state).api.updatePassword;
export const changePasswordApiSelector = (state: RootState) => authStateSelector(state).api.changePassword;
export const changeEmailApiSelector = (state: RootState) => authStateSelector(state).api.changeEmail;

/**
 * The address Supabase auth holds — what sign-in and password reset actually use. Distinct from
 * `userEmailSelector`, which prefers the CensorMe profile row for display; the two can disagree
 * while an email change is waiting on its confirmation link.
 */
export const authEmailSelector = createSelector(
  [userSelector],
  user => user?.email || ''
);

/** A confirmed-but-unapplied email change: Supabase holds the new address on `new_email`. */
export const pendingEmailSelector = createSelector(
  [userSelector],
  user => (user?.new_email as string | undefined) || ''
);

export const isAuthenticatedSelector = createSelector(
  [userSelector],
  user => user !== null
);

export const userEmailSelector = createSelector(
  [userSelector, appUserSelector],
  (user, appUser) => appUser?.email || user?.email || ''
);

/**
 * Does this account still need to choose a password?
 *
 * An account provisioned from a Paddle checkout has never had one — the emailed login link is the
 * only way in until it does — so the set-password screen has to go in front of it before anything
 * else. Three sources, in falling order of authority:
 *
 *   1. `passwordSetConfirmed`, set by our own successful write. Nothing else is this current.
 *   2. `password_set` on the profile row, once `GET /rest/v1/user/profile` has answered.
 *   3. `censor_me_password_set` on the access token, which is there the instant the link signs them
 *      in — before any profile request lands, which is exactly when the gate has to work.
 *
 * Unknown means **no gate**. Claims were absent from every token issued before the backend fixed
 * that, and a cached profile from before the column existed has no `password_set`; treating either
 * as false would strand existing users on a screen asking them to re-choose a password they have.
 */
export const needsPasswordSetupSelector = createSelector(
  [passwordSetConfirmedSelector, appUserSelector, sessionSelector],
  (confirmed, appUser, session): boolean => {
    if (confirmed) {
      return false;
    }
    if (typeof appUser?.password_set === 'boolean') {
      return !appUser.password_set;
    }

    const claims = decodeCensorMeClaims(session?.access_token);
    return claims?.censor_me_password_set === false;
  }
);

export const userDisplayNameSelector = createSelector(
  [userSelector, appUserSelector],
  (user, appUser) => appUser?.name || user?.user_metadata?.name || user?.user_metadata?.full_name || ''
);

/** The editor this user cuts in, or `null` for "never asked" — see `backend-types/user.ts`. */
export const videoEditorSelector = createSelector(
  [appUserSelector],
  appUser => appUser?.video_editor ?? null
);

/**
 * Does this account still need to answer the video editor question? True whenever the profile has
 * loaded and `video_editor` is still null — whether that's a brand-new account or an existing one
 * that cleared its answer from Settings. Re-asked on every sign-in until it's set, same as the
 * password gate re-asks until `password_set` flips.
 *
 * Gated on `appUser` having loaded at all: before the profile read lands there is nothing to answer
 * yet, and showing the gate off a `null` that just means "haven't fetched it" would flash it in front
 * of every user for a moment on every load.
 */
export const needsVideoEditorSelector = createSelector(
  [appUserSelector],
  appUser => appUser !== null && (appUser.video_editor ?? null) === null
);

/**
 * Whether the convert dialog should push harder for a full media path than for the editors that
 * flag a clip offline by themselves. True for DaVinci Resolve **and** for a user we've never asked,
 * since an editor we haven't characterised is described in the more cautious terms.
 *
 * This decides **wording only** — nothing blocks a conversion on it. The field falls back to the
 * uploaded filename, which every editor can still be pointed at after import. (The backend helper
 * it wraps is still called `requiresStrictMediaReference`; `backend-types/` is generated and
 * read-only, so the name outlived the blocking behaviour it was written for.)
 *
 * Read this rather than comparing `videoEditorSelector`'s value to a literal anywhere else. Passes
 * only the one field the helper reads — `AppUser`'s looser typing (carried over from the desktop
 * app) doesn't structurally match `Partial<User>`.
 */
export const prefersFullMediaPathSelector = createSelector(
  [videoEditorSelector],
  videoEditor => requiresStrictMediaReference({video_editor: videoEditor})
);
